/**
 * Copyright SimVentions, Inc. Usage, distribution, transferal, and licensing
 * of this source code is protected under SBIR law as described in DFARS 252.227-7018.
 *
 * SBIR data rights fully described in the README.md file in the top level directory of this project.
 */
import { FileType } from "Api";
import { QuickFormFile } from "./QuickFormTypes";

/**
 * Represents a validation rule that can be applied to a QuickForm file
 */
export interface ValidationRule {
  /** Unique identifier for this rule */
  id: string;

  /** Human-readable name of the rule */
  name: string;

  /** Detailed description of what the rule checks */
  description: string;

  /** Check if the file violates this rule */
  validate: (file: QuickFormFile) => boolean;

  /** Get a user-friendly error message when the rule is violated */
  getErrorMessage: (file: QuickFormFile) => string;
}

/**
 * Rule: Input type cannot equal output type
 */
export class NoSameTypeTransformRule implements ValidationRule {
  id = "no-same-type-transform";
  name = "Different Types Required";
  description = "Input and output file types must be different";

  validate(file: QuickFormFile): boolean {
    if (!file.detectedType || !file.transformTo) {
      return true; // Not ready to validate
    }
    return file.detectedType !== file.transformTo;
  }

  getErrorMessage(file: QuickFormFile): string {
    return `Cannot transform ${file.detectedType} to ${file.transformTo}. Input and output types must be different.`;
  }
}

/**
 * Rule: Transform target must be set
 */
export class TransformTargetRequiredRule implements ValidationRule {
  id = "transform-target-required";
  name = "Transform Target Required";
  description = "A target transformation type must be selected";

  validate(file: QuickFormFile): boolean {
    return file.transformTo !== null && file.transformTo !== undefined;
  }

  getErrorMessage(_file: QuickFormFile): string {
    return "Please select a transform type before transforming this file.";
  }
}

/**
 * Rule: File type must be detected
 */
export class FileTypeDetectedRule implements ValidationRule {
  id = "file-type-detected";
  name = "File Type Detected";
  description = "File type must be detected before transformation";

  validate(file: QuickFormFile): boolean {
    return (
      file.detectedType !== null &&
      file.detectedType !== undefined &&
      file.detectedType !== FileType.UNDETECTED
    );
  }

  getErrorMessage(_file: QuickFormFile): string {
    return "File type could not be detected. Please manually select the file type.";
  }
}

/**
 * Rule: Only specific transform pairs are supported
 */
export class SupportedTransformPairRule implements ValidationRule {
  id = "supported-transform-pair";
  name = "Supported Transform";
  description = "Only certain file type transformations are supported";

  private supportedPairs: Array<[FileType, FileType]> = [
    [FileType.PROTO, FileType.XML],
    [FileType.XML, FileType.PROTO],
    [FileType.PROTO, FileType.CPP],
    // Add more supported pairs here as new transforms are added
  ];

  validate(file: QuickFormFile): boolean {
    if (!file.detectedType || !file.transformTo) {
      return true; // Not ready to validate
    }

    return this.supportedPairs.some(
      ([from, to]) => file.detectedType === from && file.transformTo === to
    );
  }

  getErrorMessage(file: QuickFormFile): string {
    return `Transform from ${file.detectedType} to ${file.transformTo} is not currently supported.`;
  }
}

/**
 * Validation result for a file
 */
export interface ValidationResult {
  isValid: boolean;
  violations: Array<{
    rule: ValidationRule;
    message: string;
  }>;
}

/**
 * Rule engine that evaluates all rules against a file
 */
export class QuickFormRuleEngine {
  private rules: ValidationRule[];

  constructor() {
    // Register all rules here
    this.rules = [
      new FileTypeDetectedRule(),
      new TransformTargetRequiredRule(),
      new NoSameTypeTransformRule(),
      new SupportedTransformPairRule(),
    ];
  }

  /**
   * Add a new rule to the engine
   */
  addRule(rule: ValidationRule): void {
    this.rules.push(rule);
  }

  /**
   * Validate a file against all rules
   */
  validate(file: QuickFormFile): ValidationResult {
    const violations: Array<{ rule: ValidationRule; message: string }> = [];

    for (const rule of this.rules) {
      if (!rule.validate(file)) {
        violations.push({
          rule,
          message: rule.getErrorMessage(file),
        });
      }
    }

    return {
      isValid: violations.length === 0,
      violations,
    };
  }

  /**
   * Get all registered rules
   */
  getRules(): ValidationRule[] {
    return [...this.rules];
  }
}

// Export singleton instance
export const ruleEngine = new QuickFormRuleEngine();
